import { computed, type Ref, watch } from 'vue';
import { useStorage } from '@vueuse/core';
import { SignStatus } from '@/types/enums/SignStatus';
import { useRoute } from 'vue-router';

export type RedirectAfterSignEntry = {
  url: string;
  createdAt: string;
  signStatus: SignStatus;
};

export type RedirectAfterSignStore = Record<string, RedirectAfterSignEntry>; // key = documentId

export type UseRedirectAfterSignOptions = {
  storageKey?: string;
  maxAgeDays?: number;
  maxEntries?: number;
  allowedProtocols?: Array<'http:' | 'https:'>;
  pruneIfStatusIn?: SignStatus[];
};

const DEFAULT_OPTIONS: Required<UseRedirectAfterSignOptions> = {
  storageKey: 'redirectAfterSign',
  maxAgeDays: 30,
  maxEntries: 100,
  allowedProtocols: ['http:', 'https:'],
  pruneIfStatusIn: [SignStatus.SIGNED, SignStatus.REJECTED]
};

function isAbsoluteUrlAllowed(
  urlString: string,
  allowedProtocols: string[]
): boolean {
  try {
    const url = new URL(urlString);
    return allowedProtocols.includes(url.protocol);
  } catch {
    return false;
  }
}

function pickFirstQueryValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getRedirectEntry(value: unknown): RedirectAfterSignEntry | undefined {
  if (!isRecord(value)) return undefined;

  const url = value.url;
  const createdAt = value.createdAt;
  const signStatus = value.signStatus;

  if (typeof url !== 'string') return undefined;
  if (typeof createdAt !== 'string') return undefined;

  const parsedStatus =
    typeof signStatus === 'string' &&
    (Object.values(SignStatus) as string[]).includes(signStatus)
      ? (signStatus as SignStatus)
      : SignStatus.PENDING;

  return { url, createdAt, signStatus: parsedStatus };
}

export function useRedirectAfterSign(
  params: {
    documentId: Ref<string | undefined>;
    redirectUrlQueryValue: Ref<unknown>;
    signStatus?: Ref<SignStatus | undefined>;
  },
  options?: UseRedirectAfterSignOptions
) {
  const cfg = { ...DEFAULT_OPTIONS, ...(options ?? {}) };

  const route = useRoute();
  const rawStore = useStorage<Record<string, unknown>>(
    cfg.storageKey,
    {},
    localStorage
  );

  const hasTerminalStatus = (status: SignStatus | undefined) =>
    status === SignStatus.SIGNED ||
    status === SignStatus.REJECTED ||
    status === SignStatus.APPROVED;

  const store = computed<RedirectAfterSignStore>(() => {
    const raw = rawStore.value;
    if (!isRecord(raw)) return {};

    const next: RedirectAfterSignStore = {};
    for (const [docId, entry] of Object.entries(raw)) {
      const parsed = getRedirectEntry(entry);
      if (!parsed) continue;
      next[docId] = parsed;
    }
    return next;
  });

  const redirectUrl = computed<string>(() => {
    const raw = route.query['redirect-url'];
    const fromQuery = Array.isArray(raw) ? raw[0] : raw;

    if (typeof fromQuery === 'string' && fromQuery.trim() !== '') {
      if (isAbsoluteUrlAllowed(fromQuery, cfg.allowedProtocols))
        return fromQuery;
    }

    const docId = params.documentId.value;
    if (!docId) return '';

    const entry = store.value[docId];
    if (!entry?.url) return '';

    return isAbsoluteUrlAllowed(entry.url, cfg.allowedProtocols)
      ? entry.url
      : '';
  });

  function prune() {
    const current = store.value;

    const nowMs = Date.now();
    const maxAgeMs = cfg.maxAgeDays * 24 * 60 * 60 * 1000;

    const entries = Object.entries(current)
      .map(([docId, entry]) => {
        const createdMs = Date.parse(entry.createdAt);
        return { docId, entry, createdMs };
      })
      .filter((x) => {
        if (cfg.pruneIfStatusIn.includes(x.entry.signStatus)) return false;
        return Number.isFinite(x.createdMs) && nowMs - x.createdMs <= maxAgeMs;
      })
      .sort((a, b) => a.createdMs - b.createdMs);

    const trimmed =
      entries.length > cfg.maxEntries
        ? entries.slice(entries.length - cfg.maxEntries)
        : entries;

    const nextStore: Record<string, unknown> = {};
    for (const { docId, entry } of trimmed) {
      nextStore[docId] = entry;
    }

    const prev = rawStore.value;
    const prevNormalized = isRecord(prev) ? prev : {};
    const nextKeys = Object.keys(nextStore);
    const prevKeys = Object.keys(prevNormalized);

    const sameLength = prevKeys.length === nextKeys.length;
    const same =
      sameLength &&
      nextKeys.every((k) => {
        const a = getRedirectEntry(prevNormalized[k]);
        const b = getRedirectEntry(nextStore[k]);
        return a?.url === b?.url && a?.createdAt === b?.createdAt;
      });

    if (!same) rawStore.value = nextStore;
  }

  function updateRecord(
    documentId: string,
    url: string,
    signStatus: SignStatus
  ) {
    if (!isAbsoluteUrlAllowed(url, cfg.allowedProtocols)) return;

    const base = isRecord(rawStore.value) ? rawStore.value : {};
    rawStore.value = {
      ...base,
      [documentId]: {
        url,
        createdAt: new Date().toISOString(),
        signStatus
      }
    };
  }

  function setStatus(documentId: string, signStatus: SignStatus) {
    const base = rawStore.value;
    if (!isRecord(base)) return;

    const existing = getRedirectEntry(base[documentId]);
    if (!existing) return;

    // Only write if something actually changed.
    if (existing.signStatus === signStatus) return;

    base[documentId] = {
      ...existing,
      signStatus
    };

    rawStore.value = { ...base };
  }

  async function remove(documentId: string): Promise<void> {
    const base = rawStore.value;
    if (!isRecord(base)) return;
    if (!(documentId in base)) return;

    const { [documentId]: _removed, ...rest } = base;
    rawStore.value = rest;

    await Promise.resolve();
  }

  async function redirectForDocument(documentId: string): Promise<void> {
    const entry = store.value[documentId];
    const urlToRedirect = entry?.url ? `${entry.url}` : '';

    if (!isAbsoluteUrlAllowed(urlToRedirect, cfg.allowedProtocols)) return;

    await remove(documentId);

    window.location.href = urlToRedirect;
  }

  watch(
    () => [
      params.documentId.value,
      params.redirectUrlQueryValue.value,
      params.signStatus?.value
    ],
    () => {
      const docId = params.documentId.value;
      if (!docId) return;

      const status = params.signStatus?.value;

      const redirectUrl = pickFirstQueryValue(
        params.redirectUrlQueryValue.value
      );
      if (redirectUrl) {
        updateRecord(docId, redirectUrl, status ?? SignStatus.PENDING);
        return;
      }

      if (status) {
        setStatus(docId, status);
      }
    },
    { immediate: true }
  );

  watch(
    () =>
      [params.documentId.value, params.signStatus?.value, route.name] as const,
    async (
      [docId, status, routeName],
      [prevDocId, prevStatus, prevRouteName]
    ) => {
      if (!docId) return;

      const isWatchedRoute =
        routeName === 'p-document-view' ||
        routeName === 'document-details' ||
        routeName === 'sign-id';

      const statusChangedForDoc =
        prevDocId === docId ? prevStatus !== status : true;

      const routeChanged = prevRouteName !== routeName;

      const shouldConsiderRedirect =
        (hasTerminalStatus(status) && statusChangedForDoc) ||
        (isWatchedRoute && routeChanged);

      if (!shouldConsiderRedirect) return;

      const entry = store.value[docId];
      if (!entry?.url) return;
      if (!isAbsoluteUrlAllowed(entry.url, cfg.allowedProtocols)) return;

      await new Promise((resolve) => setTimeout(resolve, 3000));

      await redirectForDocument(docId);
    }
  );

  // Prune once on composable init
  prune();

  return {
    store,
    redirectUrl,
    remove,
    redirectForDocument
  };
}
